<template>
    <v-dialog :value="value" width="720" persistent scrollable>
        <v-card>
            <v-card-title class="text-h6">
                {{ $t('unit.create.title') }}
                <span class="text-body-2 grey--text ml-3">
                    {{ stepLabel }}
                </span>
            </v-card-title>
            <v-divider></v-divider>

            <v-card-text class="pt-4">
                <!-- 步驟一：選模式。刻意用兩張卡片而不是藏在表單裡的下拉——
                     模式決定了後面整套編輯流程，而且不可逆（brief §7） -->
                <template v-if="step === 'mode'">
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-card outlined class="mode-card pa-4" @click="chooseClassic">
                                <div class="text-subtitle-1 font-weight-medium">
                                    {{ $t('unit.create.mode_classic') }}
                                </div>
                                <div class="text-body-2 grey--text text--darken-1 mt-2">
                                    {{ $t('unit.create.mode_classic_desc') }}
                                </div>
                            </v-card>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-card outlined class="mode-card pa-4" @click="chooseUnit">
                                <div class="text-subtitle-1 font-weight-medium">
                                    {{ $t('unit.create.mode_unit') }}
                                </div>
                                <div class="text-body-2 grey--text text--darken-1 mt-2">
                                    {{ $t('unit.create.mode_unit_desc') }}
                                </div>
                            </v-card>
                        </v-col>
                    </v-row>
                    <div class="text-caption orange--text text--darken-3 mt-3">
                        {{ $t('unit.create.irreversible') }}
                    </div>

                    <!-- 別的老師分享的 tp_ 代碼：深拷貝一份自己的範本（studio-api docs/plans/TemplateShare.spec.md）。
                         模式與地圖都跟著原範本，所以不走下面的兩步 -->
                    <v-divider class="my-4"></v-divider>
                    <div class="d-flex align-center">
                        <span class="text-body-2 grey--text text--darken-1">
                            {{ $t('unit.create.share_code_prompt') }}
                        </span>
                        <v-btn text small color="primary" class="normal-case ml-1" @click="chooseShareCode">
                            {{ $t('unit.create.share_code_enter') }}
                        </v-btn>
                    </div>
                </template>

                <!-- 用分享代碼建立 -->
                <template v-else-if="step === 'code'">
                    <div class="text-body-2 mb-3">{{ $t('unit.create.share_code_message') }}</div>
                    <v-text-field v-model="shareCode" outlined autofocus
                        :label="$t('unit.create.share_code_label')"
                        placeholder="tp_XXXXXXXX"
                        :error-messages="shareCodeError"
                        @input="shareCodeError = ''"
                        @keyup.enter="claimShareCode"></v-text-field>
                </template>

                <!-- 步驟二：選地圖。只有縮圖與關卡數，不顯示名稱與 id（brief §3：
                     map 表沒有 name，而地圖本身是視覺化的東西，看圖選比看號碼選直觀） -->
                <template v-else>
                    <div class="text-body-2 mb-3">{{ $t('unit.create.pick_map') }}</div>

                    <div v-if="isLoadingMaps" class="text-center py-8">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>
                    <div v-else-if="mapLoadFailed" class="text-center py-8">
                        <div class="grey--text mb-3">{{ $t('unit.create.map_load_failed') }}</div>
                        <v-btn outlined small class="normal-case" @click="loadMaps">
                            {{ $t('unit.list.reload') }}
                        </v-btn>
                    </div>
                    <div v-else-if="!maps.length" class="text-center py-8 grey--text">
                        {{ $t('unit.create.no_map') }}
                    </div>
                    <v-row v-else>
                        <v-col v-for="m in maps" :key="m.map_id" cols="6" sm="4" md="3">
                            <v-card outlined class="map-card"
                                :class="{ 'map-card--active': m.map_id === selectedMapId }"
                                @click="selectedMapId = m.map_id">
                                <v-img :src="m.background_url" aspect-ratio="1" class="grey lighten-3">
                                    <template v-slot:placeholder>
                                        <div class="fill-height d-flex align-center justify-center">
                                            <v-progress-circular indeterminate size="20" width="2"
                                                color="grey lighten-1"></v-progress-circular>
                                        </div>
                                    </template>
                                </v-img>
                                <div class="text-caption grey--text text-center py-1">
                                    {{ $t('unit.create.map_units', { n: m.max_units }) }}
                                </div>
                            </v-card>
                        </v-col>
                    </v-row>

                    <div class="text-caption orange--text text--darken-3 mt-3">
                        {{ $t('unit.create.irreversible') }}
                    </div>
                </template>
            </v-card-text>

            <v-divider></v-divider>
            <v-card-actions>
                <v-btn v-if="step !== 'mode'" text class="normal-case" :disabled="isClaiming"
                    @click="step = 'mode'">
                    {{ $t('button.back') }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn text class="normal-case" :disabled="isClaiming" @click="close">
                    {{ $t('button.cancel') }}
                </v-btn>
                <v-btn v-if="step === 'code'" color="primary" depressed class="normal-case"
                    :disabled="!shareCode.trim()" :loading="isClaiming" @click="claimShareCode">
                    {{ $t('unit.create.share_code_create') }}
                </v-btn>
                <v-btn v-if="step === 'map'" color="primary" depressed class="normal-case"
                    :disabled="!selectedMapId" @click="confirmMap">
                    {{ $t('unit.create.next') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { templateUnitServices } from '@/services/templateUnit';

/**
 * 建立範本的兩步流程（brief §3 / §7、spec §7）。
 *
 * 步驟一選模式、步驟二選地圖，兩者**建立後都不可變更**，所以兩步都寫明。
 * 模式刻意用兩張卡片呈現而不是藏在建立表單裡的下拉——它決定了後面整套編輯流程，
 * 藏起來老師不會意識到自己做了不可逆的選擇。
 *
 * 這個對話框**只負責選擇**，實際建立仍走既有的 `SampleHomeworkEdit`：
 * 傳統範本導向 `/diarySample/new`，關卡式導向 `/diarySample/new?map_id=N`，
 * 由那一頁把 `map_id` 帶進 createHomework 的 payload。後端只有在
 * 新建且帶 map_id 時才寫入預設 template（impl-plan §7）。
 *
 * 另一條路是步驟一下方的「用分享代碼建立」：深拷貝別人分享的範本，成功後導到
 * 範本設定頁 `/diarySample/:id/edit?next=units`。名稱照原名，讓老師自己改；
 * 存檔後設定頁接著導到關卡編輯頁（TemplateShare.spec.md 決策 5）。
 */
export default {
    name: 'CreateTemplateDialog',
    props: {
        value: { type: Boolean, default: false },
    },
    data() {
        return {
            step: 'mode',
            maps: [],
            selectedMapId: null,
            isLoadingMaps: false,
            mapLoadFailed: false,
            shareCode: '',
            shareCodeError: '',
            isClaiming: false,
        };
    },
    computed: {
        stepLabel() {
            switch (this.step) {
                case 'map': return this.$t('unit.create.step_map');
                case 'code': return this.$t('unit.create.step_code');
                default: return this.$t('unit.create.step_mode');
            }
        },
    },
    watch: {
        value(open) {
            if (open) {
                this.step = 'mode';
                this.selectedMapId = null;
                this.shareCode = '';
                this.shareCodeError = '';
            }
        },
    },
    methods: {
        chooseClassic() {
            this.close();
            this.$router.push('/diarySample/new');
        },

        async chooseUnit() {
            this.step = 'map';
            if (!this.maps.length) {
                await this.loadMaps();
            }
        },

        async loadMaps() {
            this.isLoadingMaps = true;
            this.mapLoadFailed = false;
            try {
                const res = await templateUnitServices.getAvailableMaps();
                this.maps = res?.data?.maps || [];
                /// 只有一張時直接選起來，省一次點擊
                if (this.maps.length === 1) {
                    this.selectedMapId = this.maps[0].map_id;
                }
            } catch (err) {
                /// 呼叫端自己 catch（spec §9）
                this.mapLoadFailed = true;
            } finally {
                this.isLoadingMaps = false;
            }
        },

        chooseShareCode() {
            this.shareCodeError = '';
            this.step = 'code';
        },

        async claimShareCode() {
            if (!this.shareCode.trim() || this.isClaiming) {
                return;
            }
            this.isClaiming = true;
            try {
                const res = await templateUnitServices.claimTemplateShareCode(this.shareCode);
                this.close();
                /// 先到設定頁讓老師改名，存檔後由設定頁接著導到關卡編輯（next=units）
                this.$router.push(`/diarySample/${res.data.diary_homework_id}/edit?next=units`);
            } catch (err) {
                /// 失敗時對話框不關，錯誤顯示在輸入框下方，老師可以直接改代碼重送
                switch (err.status) {
                    case 400:
                    case 404:
                        this.shareCodeError = this.$t('unit.create.share_code_error');
                        break;
                    case 409:
                        this.shareCodeError = this.$t('unit.create.share_has_sub_map');
                        break;
                    default:
                        this.shareCodeError = this.$t('unit.create.share_claim_failed');
                }
            } finally {
                this.isClaiming = false;
            }
        },

        confirmMap() {
            if (!this.selectedMapId) {
                return;
            }
            this.close();
            this.$router.push(`/diarySample/new?map_id=${this.selectedMapId}`);
        },

        close() {
            this.$emit('input', false);
        },
    },
};
</script>

<style scoped>
.mode-card {
    cursor: pointer;
    height: 100%;
    transition: border-color 0.15s, box-shadow 0.15s;
}

.mode-card:hover {
    border-color: var(--v-primary-base, #1976D2);
}

.map-card {
    cursor: pointer;
    overflow: hidden;
}

.map-card--active {
    border-color: var(--v-primary-base, #1976D2);
    box-shadow: 0 0 0 2px var(--v-primary-base, #1976D2) inset;
}
</style>
